/**
 * ToastContext - Enhanced toast with animations and positioning
 */

// ✅ FIXED: Add useEffect to imports
import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react'
import { CheckCircle, AlertTriangle, Info, X } from 'lucide-react'

const ToastContext = createContext(null)

const ICONS = {
  success: { Icon: CheckCircle, color: 'text-success', bg: 'bg-success-soft' },
  error: { Icon: AlertTriangle, color: 'text-error', bg: 'bg-error-soft' },
  warning: { Icon: AlertTriangle, color: 'text-warning', bg: 'bg-warning-soft' },
  info: { Icon: Info, color: 'text-info', bg: 'bg-info-soft' },
}

const POSITIONS = {
  'top-right': 'top-6 right-6',
  'top-left': 'top-6 left-6',
  'top-center': 'top-6 left-1/2 -translate-x-1/2',
  'bottom-right': 'bottom-6 right-6',
  'bottom-left': 'bottom-6 left-6',
  'bottom-center': 'bottom-6 left-1/2 -translate-x-1/2',
}

let toastId = 0

function ToastItem({ id, message, type = 'info', duration = 4000, onRemove, position = 'bottom-right' }) {
  const [isExiting, setIsExiting] = useState(false)
  const { Icon, color, bg } = ICONS[type] || ICONS.info

  const handleRemove = useCallback(() => {
    setIsExiting(true)
    setTimeout(() => onRemove(id), 300)
  }, [id, onRemove])

  // ✅ useEffect is now imported
  useEffect(() => {
    if (duration === 0) return
    const timer = setTimeout(handleRemove, duration)
    return () => clearTimeout(timer)
  }, [duration, handleRemove])

  // Position-specific animation
  const getEnterAnimation = () => {
    if (position.includes('right')) return 'translate-x-20'
    if (position.includes('left')) return '-translate-x-20'
    if (position.includes('center')) return '-translate-y-20'
    return 'translate-x-20'
  }

  return (
    <div
      className={`
        flex items-start gap-3
        bg-surface border-l-4 border-l-${type}
        rounded-lg shadow-lg
        px-4 py-3
        min-w-[280px] max-w-sm
        transition-all duration-300 ease-out
        ${isExiting ? 'opacity-0 translate-x-20 scale-95' : 'opacity-100 translate-x-0 scale-100'}
        ${position.includes('center') ? 'text-center' : ''}
      `}
      style={{
        borderLeftColor: type === 'success' ? '#059669' : 
                        type === 'error' ? '#E11D48' :
                        type === 'warning' ? '#D97706' : '#2563EB'
      }}
      role="alert"
    >
      <div className={`${bg} rounded-full p-1.5 shrink-0 mt-0.5`}>
        <Icon size={16} className={color} />
      </div>
      <p className="text-sm text-ink flex-1">{message}</p>
      <button
        onClick={handleRemove}
        className="shrink-0 text-neutral-400 hover:text-ink transition-colors"
        aria-label="Close notification"
      >
        <X size={14} />
      </button>
    </div>
  )
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])
  const [position, setPosition] = useState('bottom-right')

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  const addToast = useCallback((message, type = 'info', duration = 4000) => {
    const id = ++toastId
    setToasts(prev => [...prev, { id, message, type, duration }])
    return id
  }, [])

  const toast = addToast

  const success = useCallback((msg, dur) => addToast(msg, 'success', dur), [addToast])
  const error = useCallback((msg, dur) => addToast(msg, 'error', dur), [addToast])
  const warning = useCallback((msg, dur) => addToast(msg, 'warning', dur), [addToast])
  const info = useCallback((msg, dur) => addToast(msg, 'info', dur), [addToast])

  // Toast with custom position
  const toastAt = useCallback((position, message, type = 'info', duration = 4000) => {
    setPosition(position)
    return addToast(message, type, duration)
  }, [addToast])

  // Toast with action button
  const toastWithAction = useCallback((message, action, type = 'info', duration = 4000) => {
    // Implementation can be extended
    return addToast(message, type, duration)
  }, [addToast])

  return (
    <ToastContext.Provider value={{ 
      toast, success, error, warning, info, 
      toastAt, toastWithAction,
      addToast 
    }}>
      {children}
      
      {/* Toast Container */}
      <div className={`fixed z-[100] flex flex-col gap-2 max-w-sm w-full pointer-events-none ${POSITIONS[position] || POSITIONS['bottom-right']}`}>
        {toasts.map((t) => (
          <div key={t.id} className="pointer-events-auto w-full">
            <ToastItem
              id={t.id}
              message={t.message}
              type={t.type}
              duration={t.duration}
              onRemove={removeToast}
              position={position}
            />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    // Return a no-op version for fallback
    return {
      toast: () => {},
      success: () => {},
      error: () => {},
      warning: () => {},
      info: () => {},
      toastAt: () => {},
      toastWithAction: () => {},
      addToast: () => {},
    }
  }
  return ctx
}

export default ToastProvider